import React, { createContext, useReducer, useContext } from 'react';

// Define initial state
const initialState = {
  connectionState: 'idle', // 'idle', 'connecting', 'connected', 'disconnected', 'error'
  transcript: [],          // Array of transcript items
  audioLevels: {
    user: 0,               // 0-100 representing current user audio level
    ai: 0                  // 0-100 representing current AI audio level
  },
  error: null              // Error object if any
};

// Create context
const WebRTCContext = createContext();

// Debug flag
const DEBUG = process.env.NODE_ENV !== 'production';

// Define reducer
function webRTCReducer(state, action) {
  if (DEBUG) {
    // Skip logging audio level updates to avoid console spam
    if (!action.type.includes('AUDIO_LEVEL')) {
      console.log(`%c[CONTEXT] ${action.type}`, 'color: gray; font-weight: bold', action.payload);
    }
  }
  
  switch (action.type) {
    case 'SET_CONNECTION_STATE':
      if (DEBUG) {
        console.log(`%c[CONTEXT] Connection state changed: ${state.connectionState} -> ${action.payload}`, 
          'color: teal; font-weight: bold');
      }
      return { ...state, connectionState: action.payload };
      
    case 'ADD_TRANSCRIPT_ITEM':
      return { ...state, transcript: [...state.transcript, action.payload] };
      
    case 'SET_USER_AUDIO_LEVEL':
      return { ...state, audioLevels: { ...state.audioLevels, user: action.payload } };
      
    case 'SET_AI_AUDIO_LEVEL':
      return { ...state, audioLevels: { ...state.audioLevels, ai: action.payload } };
      
    case 'SET_ERROR':
      if (DEBUG) {
        console.error('%c[CONTEXT] Error set:', 'color: red; font-weight: bold', action.payload);
      }
      return { ...state, error: action.payload, connectionState: 'error' };
      
    case 'RESET':
      if (DEBUG) {
        console.log('%c[CONTEXT] State reset to initial', 'color: orange; font-weight: bold');
      }
      return initialState;
      
    default:
      return state;
  }
}

// Create provider component
export function WebRTCProvider({ children }) {
  const [state, dispatch] = useReducer(webRTCReducer, initialState);
  
  return (
    <WebRTCContext.Provider value={{ state, dispatch }}>
      {children}
    </WebRTCContext.Provider>
  );
}

// Custom hook for using the context
export function useWebRTCContext() {
  const context = useContext(WebRTCContext);
  if (context === undefined) {
    throw new Error('useWebRTCContext must be used within a WebRTCProvider');
  }
  return context;
} 